import type { MemberAccountLogVO } from "@/api/member/type";

/** 账户摘要卡筛选 key */
export type AccountCardKey = "balance" | "gift" | "credit" | "points" | "growth" | "coupon";

/** 摘要卡中文标签（用于明细标题与维度高亮） */
export const ACCOUNT_CARD_LABELS: Record<AccountCardKey, string> = {
  balance: "本金",
  gift: "赠送金",
  credit: "授信",
  points: "积分",
  growth: "成长值",
  coupon: "卡券",
};

/**
 * 金额/数量格式化：保留两位小数（整数不加小数也可读）
 * @param value 数值
 */
export const formatAmount = (value?: number) => {
  if (value === undefined || value === null || Number.isNaN(value)) return "-";
  return value.toLocaleString("zh-CN", {
    minimumFractionDigits: Number.isInteger(value) ? 0 : 2,
    maximumFractionDigits: 2,
  });
};

/**
 * 带正负号的变动展示；0 显示为「-」
 * @param value 变动值
 */
export const formatDelta = (value?: number) => {
  if (value === undefined || value === null || Number.isNaN(value)) return "-";
  if (value === 0) return "-";
  if (value > 0) return `+${formatAmount(value)}`;
  return formatAmount(value);
};

/**
 * 变动明细右上角合计：优先本金+赠送金，均为 0 则用积分
 * @param principalDelta 本金变动
 * @param bonusDelta 赠送金变动
 * @param pointsDelta 积分变动
 */
export const getAccountLogTotalDelta = (
  principalDelta?: number,
  bonusDelta?: number,
  pointsDelta?: number,
) => {
  const principal = principalDelta ?? 0;
  const bonus = bonusDelta ?? 0;
  if (principal !== 0 || bonus !== 0) return principal + bonus;
  return pointsDelta ?? 0;
};

/**
 * 判断流水是否命中某摘要卡维度（变动非 0）
 * @param log 流水
 * @param cardKey 摘要卡 key
 */
export const isAccountLogMatchCard = (log: MemberAccountLogVO, cardKey: AccountCardKey) => {
  if (cardKey === "balance") return (log.principalDelta ?? 0) !== 0;
  if (cardKey === "gift") return (log.bonusDelta ?? 0) !== 0;
  if (cardKey === "credit") return (log.creditLimitDelta ?? 0) !== 0;
  if (cardKey === "points") return (log.pointsDelta ?? 0) !== 0;
  if (cardKey === "growth") return (log.growthValueDelta ?? 0) !== 0;
  if (cardKey === "coupon") return (log.couponDelta ?? 0) !== 0;
  return true;
};

/**
 * 摘要卡维度对应的流水字段中文标签（用于高亮列）
 * @param cardKey 摘要卡 key
 */
export const getCardDimensionLabel = (cardKey: AccountCardKey) => {
  if (cardKey === "growth") return "成长";
  if (cardKey === "points") return "积分";
  return ACCOUNT_CARD_LABELS[cardKey];
};
